/**
 * Top-level resilience for the kiosk player (#178).
 *
 * A Raspberry Pi in a shop window has no keyboard and no operator: any
 * uncaught render error would otherwise leave a permanent black (or blank
 * React) screen until someone physically power-cycles the device. This module
 * provides:
 *
 *  1. `ErrorBoundary` — a class component that catches render/lifecycle errors,
 *     shows a minimal recovery message, and schedules a page reload.
 *  2. `scheduleReloadWithBackoff` — the shared reload trigger, also used by the
 *     global `error` / `unhandledrejection` handlers in `main.tsx`. It backs
 *     off exponentially when the page keeps reloading within a short window so
 *     a persistent crash does not become a tight reload loop.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

// ─── Reload-with-backoff ─────────────────────────────────────────────────────

/** sessionStorage key holding the rolling reload record. */
const RELOAD_STORAGE_KEY = 'player.reloadBackoff';
/** Rolling window: reloads older than this no longer count toward backoff. */
export const RELOAD_WINDOW_MS = 60_000;
/** Delay for the first few reloads inside a window (fast recovery). */
export const BASE_RELOAD_DELAY_MS = 2_000;
/** Ceiling for the backed-off delay. */
export const MAX_RELOAD_DELAY_MS = 60_000;
/** Reloads within the window before exponential backoff kicks in. */
export const RELOAD_LOOP_THRESHOLD = 3;

interface ReloadRecord {
  count: number;
  windowStart: number;
}

/** Module-level guard so repeated handler fires schedule at most one reload. */
let reloadScheduled = false;

function readReloadRecord(now: number): ReloadRecord {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ReloadRecord>;
      if (
        typeof parsed.count === 'number' &&
        typeof parsed.windowStart === 'number' &&
        now - parsed.windowStart <= RELOAD_WINDOW_MS
      ) {
        return { count: parsed.count, windowStart: parsed.windowStart };
      }
    }
  } catch {
    // Storage unavailable/corrupt — treat as a fresh window.
  }
  return { count: 0, windowStart: now };
}

function writeReloadRecord(record: ReloadRecord): void {
  try {
    window.sessionStorage.setItem(RELOAD_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Best-effort only; a failed write just means less accurate backoff.
  }
}

/**
 * Compute the reload delay for the Nth reload inside the current window. The
 * first `RELOAD_LOOP_THRESHOLD` reloads use `BASE_RELOAD_DELAY_MS`; beyond that
 * the delay doubles per reload, capped at `MAX_RELOAD_DELAY_MS`.
 */
export function computeReloadDelay(count: number): number {
  if (count <= RELOAD_LOOP_THRESHOLD) {
    return BASE_RELOAD_DELAY_MS;
  }
  const backoff =
    BASE_RELOAD_DELAY_MS * 2 ** (count - RELOAD_LOOP_THRESHOLD);
  return Math.min(backoff, MAX_RELOAD_DELAY_MS);
}

/**
 * Schedule a single page reload, backing off if the page has been reloading
 * repeatedly within {@link RELOAD_WINDOW_MS}. Safe to call multiple times — the
 * first call in a page lifetime wins.
 */
export function scheduleReloadWithBackoff(): void {
  if (reloadScheduled) return;
  reloadScheduled = true;

  const now = Date.now();
  const previous = readReloadRecord(now);
  const count = previous.count + 1;
  writeReloadRecord({ count, windowStart: previous.windowStart });

  const delay = computeReloadDelay(count);
  console.warn(
    `[Player] Scheduling recovery reload #${count} in ${delay}ms`,
  );

  setTimeout(() => {
    try {
      window.location.reload();
    } catch (err) {
      console.error('[Player] Recovery reload failed:', err);
    }
  }, delay);
}

/**
 * Reset the in-memory reload guard. Intended for tests only — production code
 * relies on the guard persisting for the lifetime of the page.
 */
export function __resetReloadGuardForTests(): void {
  reloadScheduled = false;
}

// ─── Error boundary ──────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      '[Player] Render crash caught by ErrorBoundary:',
      error,
      info.componentStack,
    );
    scheduleReloadWithBackoff();
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#000',
            color: '#666',
            fontFamily: 'system-ui, sans-serif',
            fontSize: '14px',
          }}
        >
          Recovering…
        </div>
      );
    }
    return this.props.children;
  }
}
